export const PASSWORD_RESET_INCOGNITO_HINT =
  'If you use a private or incognito window, open the email link in that same window.';

export const PASSWORD_RESET_EMAIL_SENT_MESSAGE =
  `If an account exists for that email, a reset link is on its way. Check your inbox and spam folder. The link lasts 1 hour. ${PASSWORD_RESET_INCOGNITO_HINT}`;

export const PASSWORD_RESET_LINK_MISSING_MESSAGE =
  'This page needs a valid reset link from your email. This can happen if the link was opened in a different browser or a private/incognito window. Request a new link and open it in the same window you used to request it.';

export const PASSWORD_RESET_LINK_EXPIRED_MESSAGE =
  'This reset link is invalid or has already been used. Request a new link from the login page.';

export const PASSWORD_RESET_SAME_PASSWORD_MESSAGE =
  'Your new password must be different from your current password.';

const STORAGE_KEY = 'shuttlr.passwordResetLink';

const getParam = (searchParams, hashParams, key) =>
  searchParams.get(key) || hashParams.get(key);

/**
 * Read recovery params from the current location. Hash is the older implicit
 * flow; query is token_hash (new) or PKCE code (legacy).
 */
export const parsePasswordResetLink = (location = window.location) => {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash || '';
  const search = location.search.startsWith('?') ? location.search.slice(1) : location.search || '';
  const hashParams = new URLSearchParams(hash);
  const searchParams = new URLSearchParams(search);

  const error = getParam(searchParams, hashParams, 'error')
    || getParam(searchParams, hashParams, 'error_code');
  const errorDescription = getParam(searchParams, hashParams, 'error_description');
  const type = getParam(searchParams, hashParams, 'type');
  const tokenHash = getParam(searchParams, hashParams, 'token_hash');
  const accessToken = getParam(searchParams, hashParams, 'access_token');
  const refreshToken = getParam(searchParams, hashParams, 'refresh_token');
  const code = getParam(searchParams, hashParams, 'code');

  if (error) {
    return { kind: 'error', error, errorDescription };
  }

  if (tokenHash) {
    if (type !== 'recovery') {
      return { kind: 'invalid' };
    }
    return { kind: 'token_hash', tokenHash };
  }

  if (type && type !== 'recovery') {
    return { kind: 'invalid' };
  }

  if (accessToken && refreshToken) {
    return { kind: 'implicit', accessToken, refreshToken };
  }

  if (code) {
    return { kind: 'pkce', code };
  }

  // Hosted emails still use ConfirmationURL. After PKCE exchange the tokens are
  // gone and only type=recovery (or nothing) remains.
  if (type === 'recovery') {
    return { kind: 'session_recovery' };
  }

  return { kind: 'missing' };
};

export const isUsablePasswordResetLink = (link) =>
  link?.kind === 'token_hash'
  || link?.kind === 'implicit'
  || link?.kind === 'pkce'
  || link?.kind === 'session_recovery';

const persistPasswordResetLink = (link) => {
  if (typeof sessionStorage === 'undefined') return;
  if (!link || link.kind === 'missing') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(link));
  } catch (err) {
    console.error('Could not store password reset link:', err);
  }
};

export const readStoredPasswordResetLink = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.kind ? parsed : null;
  } catch (err) {
    return null;
  }
};

export const clearStoredPasswordResetLink = () => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('Could not clear password reset link:', err);
  }
};

/** Call before createClient so detectSessionInUrl cannot wipe the URL first. */
export const capturePasswordResetLinkFromLocation = (location = window.location) => {
  const link = parsePasswordResetLink(location);
  persistPasswordResetLink(link);
  return link;
};

export const markPasswordRecoverySession = () => {
  const existing = readStoredPasswordResetLink();
  if (isUsablePasswordResetLink(existing) && existing.kind !== 'session_recovery') {
    return existing;
  }
  return promoteToRecoverySession();
};

/** After verifyOtp / code exchange, keep the session for retries (e.g. same-password). */
export const promoteToRecoverySession = () => {
  const link = { kind: 'session_recovery' };
  persistPasswordResetLink(link);
  return link;
};

export const resolvePasswordResetLink = (location = window.location) => {
  const fromUrl = parsePasswordResetLink(location);
  if (fromUrl.kind !== 'missing') {
    persistPasswordResetLink(fromUrl);
    return fromUrl;
  }
  return readStoredPasswordResetLink() || fromUrl;
};

const RESET_QUERY_KEYS = [
  'token_hash',
  'type',
  'code',
  'access_token',
  'refresh_token',
  'error',
  'error_code',
  'error_description',
];

/** Remove recovery tokens from the address bar after they are stored in memory. */
export const stripPasswordResetParams = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  RESET_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
  url.hash = '';
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
};

export const mapPasswordResetSubmitError = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();

  if (
    code.includes('same_password') ||
    (message.includes('password') && message.includes('different')) ||
    (message.includes('password') && message.includes('same as'))
  ) {
    return PASSWORD_RESET_SAME_PASSWORD_MESSAGE;
  }

  if (
    message.includes('password') &&
    (message.includes('character') || message.includes('weak') || message.includes('least'))
  ) {
    return error.message;
  }

  if (
    code.includes('otp_expired') ||
    code.includes('otp_disabled') ||
    code.includes('token') ||
    message.includes('expired') ||
    message.includes('already been used') ||
    message.includes('already used') ||
    message.includes('invalid')
  ) {
    return PASSWORD_RESET_LINK_EXPIRED_MESSAGE;
  }

  return 'Could not update your password. Please request a new link and try again.';
};

export const mapPasswordResetRequestError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  if (
    message.includes('rate') ||
    message.includes('security purposes') ||
    message.includes('too many')
  ) {
    return 'Too many reset requests. Please wait a minute and try again.';
  }
  if (message.includes('redirect')) {
    return 'Password reset is temporarily unavailable. Please try again later.';
  }
  return 'Could not send the reset email. Please try again.';
};
